import { useMutation, useQuery } from "@apollo/client/react";
import { InboxIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import { ColumnDef, getCoreRowModel } from "@tanstack/react-table";
import { Fragment, useMemo, useState } from "react";

import { ErrorFallback, Head } from "../../../../components/core";
import { Button } from "../../../../components/form";
import { LimitBy, TableCursor } from "../../../../components/table";
import {
  NotifyType,
  useNotifyContext,
} from "../../../../contexts/NotifyContext";
import {
  GET_SPECIALPRICES,
  REMOVE_SPECIALPRICE,
} from "../../../../graphql/inventory/pricing/special-prices";
import { AlertModal, AlertType, useAlert } from "../../../../hooks/useAlert";
import { formatDate } from "../../../../utils";
import { AuthShield } from "../../../auth/core";

type PricingMethodField = {
  fieldType: string;
  fieldKey: string;
  fieldName: string;
  fieldOptions: string[];
};

type PricingMethod = {
  id?: string;
  name: string;
  sellPriceFormula: string[];
  pricingMethodFields: PricingMethodField[];
  createdAt: string;
  status: boolean;
};

type Product = {
  id: string;
  name: string;
};

type SpecialPrice = {
  id: string;
  name: string;
  pricingMethod: PricingMethod;
  customerType: number;
  products: Product[];
  createdAt: string;
  status: boolean;
};

const SpecialPriceList = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
  const [pageLimit, setPageLimit] = useState<LimitBy>(LimitBy.L3);
  const { addNotify } = useNotifyContext();

  const { data, loading, error, refetch } = useQuery<{
    fetchSpecialPrices: SpecialPrice[];
  }>(GET_SPECIALPRICES);

  const specialPrices = useMemo<SpecialPrice[]>(
    () =>
      data?.fetchSpecialPrices.flatMap((item) => ({
        ...item,
        createdAt: formatDate(item.createdAt),
      })) || [],
    [data]
  );

  const [deleteSpecialPrice] = useMutation(REMOVE_SPECIALPRICE, {
    refetchQueries: [
      {
        query: GET_SPECIALPRICES,
      },
    ],
  });
  const handleDelete = async (id: string) => {
    return await deleteSpecialPrice({
      variables: {
        id,
      },
    })
      .then(({ data }) => {
        if (data?.specialPriceDelete) {
          const deletedSpecialPrice = data?.specialPriceDelete?.specialPrice;
          refetch();
          addNotify({
            type: NotifyType.SUCCESS,
            title: `Special price ${deletedSpecialPrice.name} deleted`,
          });
        } else {
          addNotify({
            type: NotifyType.ERROR,
            title: "Special price delete failed",
            message: "Something went wrong, please try again later",
          });
        }
      })
      .catch((error) => {
        addNotify({
          type: NotifyType.ERROR,
          title: "Special price delete failed",
          message: error.message,
        });
      });
  };

  type AlertProps = {
    id: string;
    name: string;
  };
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const ResponseAlert = useAlert({
    open: alert ? true : false,
    title: "Do you want to proceed with this operation?",
    message: (
      <p className="text-sm text-gray-500">
        Are you sure you wish to delete{" "}
        <b className="text-gray-900">{alert?.name}</b> permanently. This action
        can't be undone. All records related to{" "}
        <b className="text-gray-900">{alert?.name}</b> will be deleted.
      </p>
    ),
    type: AlertType.DANGER,
    modal: AlertModal.CENTERED_DOUBLE_ACTION,
    delay: 3000,
    onConfirm: async () => {
      await handleDelete(alert?.id!);
      setAlert(null);
    },
    onCancel: () => {
      setAlert(null);
    },
  });

  const columns = useMemo<ColumnDef<SpecialPrice, any>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 30,
        enableHiding: false,
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 300,
        enableHiding: false,
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
        accessorFn: (row) => row.name,
      },
      {
        accessorKey: "customerType",
        header: "Customer Type",
        cell: ({ row }) => {
          return row.original.customerType === 1 ? (
            <span className="whitespace-nowrap">Specific Customers</span>
          ) : (
            <span className="whitespace-nowrap">All Customers</span>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) =>
          info.getValue() ? (
            <span className="inline-flex rounded-full bg-green-100 px-2 text-xs font-medium leading-5 text-green-800">
              Active
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-red-100 px-2 text-xs font-medium leading-5 text-red-800">
              Inactive
            </span>
          ),
      },
      {
        id: "actions",
        header: "Actions",
        enableHiding: false,
        enableSorting: false,
        enableGlobalFilter: false,
        size: 220,
        cell: (props) => (
          <div className="flex space-x-2 md:space-x-4">
            <AuthShield access={["update-pricings"]}>
              <Button
                type="link"
                href={`/inventory/pricing/special-prices/${props.row.original.id}`}
                variant="icon"
                className="text-blue-500 hover:text-blue-600"
              >
                <PencilIcon aria-hidden="true" className="text-md h-4 w-4" />
                <span className="sr-only">Edit, {props.row.original.name}</span>
              </Button>
            </AuthShield>
            <AuthShield access={["destroy-pricings"]}>
              <Button
                variant="icon"
                className="text-red-500 hover:text-red-600"
                onClick={() => {
                  setAlert({
                    id: props.row.original.id,
                    name: props.row.original.name,
                  });
                }}
              >
                <TrashIcon aria-hidden="true" className="text-md h-4 w-4" />
                <span className="sr-only">
                  Delete, {props.row.original.name}
                </span>
              </Button>
            </AuthShield>
          </div>
        ),
      },
    ],
    []
  );

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <Head
        title="Special Prices"
        heading="Special Prices"
        breadcrumbs={[
          ...breadcrumbs,
          {
            name: "Pricing",
            href: "/inventory/pricing",
          },
          {
            name: SpecialPriceListResource.name,
            href: null,
          },
        ]}
      />
      <ResponseAlert />
      <div className="rounded-xl bg-greyish p-5">
        <div className="mb-5 sm:flex sm:items-end">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-medium text-gray-900">
              {SpecialPriceListResource.name}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {SpecialPriceListResource.description}
            </p>
          </div>
          <div className="ml-auto flex items-center space-x-2 pr-3">
            <AuthShield access={["create-pricings"]}>
              <Button type="link" href="/inventory/pricing/special-prices/add">
                <PlusCircleIcon className="mr-2 h-5 w-5" />
                Add special price
              </Button>
            </AuthShield>
          </div>
        </div>

        <TableCursor
          data={specialPrices}
          columns={columns}
          loading={loading}
          totalRows={specialPrices.length}
          pageLimit={pageLimit}
          setPageLimit={setPageLimit}
          getCoreRowModel={getCoreRowModel()}
          enableGlobalFilter={true}
          fallbackTitle="No special prices found"
          fallbackText="You can add a new special price by clicking on add special price button."
        />
      </div>
    </>
  );
};

export default SpecialPriceList;
export const SpecialPriceListResource: ResourceProps = {
  name: "Special Prices",
  description: "A list of special prices",
  access: ["read-pricings"],
  path: "special-prices",
};
